/**
 * DeadStockScreen — capital locked in slow-moving and dead inventory, over a
 * configurable sales lookback window.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { toDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadDeadStock } from '@/reports/deadStock/deadStockDataSource';
import { buildDeadStock, type DeadStockReport, type DeadStockItem } from '@/reports/deadStock/deadStock';

const WINDOWS = [30, 90, 180] as const;

// ─── Component ───────────────────────────────────────────────────────────────

export function DeadStockScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [windowDays, setWindowDays] = useState<number>(90);
  const [data, setData]       = useState<DeadStockReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const source = await loadDeadStock(activeStoreId, windowDays, 180, new Date());
      setData(buildDeadStock(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, windowDays]);

  useEffect(() => { void load(); }, [load]);

  const actionable = data ? data.items.filter((i) => i.status !== 'MOVING') : [];

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.items, [
        { header: 'Product', value: (r) => r.name },
        { header: 'Category', value: (r) => r.category },
        { header: 'Status', value: (r) => r.status },
        { header: 'Stock', value: (r) => r.currentStock },
        { header: 'Stock value (cost)', value: (r) => r.stockValue },
        { header: 'Units sold', value: (r) => r.unitsSold },
        { header: 'Days since sale', value: (r) => (r.daysSinceSale ?? '') },
        { header: 'Days of cover', value: (r) => (r.daysOfCover ?? '') },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `DeadStock_${windowDays}d_${toDateInputValue(new Date())}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildDeadStockHtml(data, actionable),
        defaultFileName: `DeadStock_${windowDays}d_${toDateInputValue(new Date())}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-fg-tertiary">Sales window:</span>
        {WINDOWS.map((w) => (
          <button key={w} type="button" onClick={() => setWindowDays(w)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              windowDays === w ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {w} days
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.items.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || actionable.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.items.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No stocked products to analyse.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Dead capital" value={formatCurrency(data.dead.stockValue)} sub={`${data.dead.count} items`} warn={data.dead.stockValue > 0} />
            <KPI label="Slow capital" value={formatCurrency(data.slow.stockValue)} sub={`${data.slow.count} items`} warn={data.slow.stockValue > 0} />
            <KPI label="Healthy stock" value={formatCurrency(data.moving.stockValue)} sub={`${data.moving.count} items`} />
            <KPI label="Total stock value" value={formatCurrency(data.totalStockValue)} accent />
          </div>

          {actionable.length === 0 ? (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-8 text-center text-body text-fg-tertiary">
              Nothing dead or slow-moving — every stocked item is turning over within {data.slowCoverDays} days.
            </div>
          ) : (
            <Card title={`Dead & slow-moving stock (no/low sales in ${data.windowDays} days)`}>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Product</th>
                      <th className="pb-1 text-center font-normal">Status</th>
                      <th className="pb-1 text-right font-normal">Stock</th>
                      <th className="pb-1 text-right font-normal">Value (cost)</th>
                      <th className="pb-1 text-right font-normal">Sold</th>
                      <th className="pb-1 text-right font-normal">Last sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionable.map((it) => (
                      <tr key={it.productId} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5">
                          <div className="text-fg-primary">{it.name}</div>
                          <div className="text-label text-fg-tertiary">{it.category}</div>
                        </td>
                        <td className="py-1.5 text-center">
                          <StatusBadge status={it.status} />
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{it.currentStock}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-primary">{formatCurrency(it.stockValue)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{it.unitsSold}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-tertiary">
                          {it.daysSinceSale == null ? 'never' : `${it.daysSinceSale}d ago`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: DeadStockItem['status'] }) {
  const map = {
    DEAD: 'bg-warning/15 text-warning-ink',
    SLOW: 'bg-accent/10 text-accent-ink',
    MOVING: 'bg-bg-subtle text-fg-tertiary',
  } as const;
  return <span className={cn('rounded px-1.5 py-0.5 text-label', map[status])}>{status.toLowerCase()}</span>;
}

function KPI({ label, value, sub, accent, warn }: { label: string; value: string; sub?: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-label text-fg-tertiary">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildDeadStockHtml(d: DeadStockReport, actionable: DeadStockItem[]): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = actionable.map((it) =>
    `<tr><td>${esc(it.name)}</td><td>${esc(it.category)}</td>
     <td>${it.status.toLowerCase()}</td>
     <td style="text-align:right">${it.currentStock}</td>
     <td style="text-align:right">${fmt(it.stockValue)}</td>
     <td style="text-align:right">${it.unitsSold}</td>
     <td style="text-align:right">${it.daysSinceSale == null ? 'never' : it.daysSinceSale + 'd'}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2),th:nth-child(3),td:nth-child(3){text-align:left}</style></head>
<body>
<h1>Dead &amp; Slow-Moving Stock</h1>
<p style="color:#888">Sales window: ${d.windowDays} days · Dead ${fmt(d.dead.stockValue)} (${d.dead.count}) · Slow ${fmt(d.slow.stockValue)} (${d.slow.count}) · Total stock ${fmt(d.totalStockValue)}</p>
<table>
<thead><tr><th>Product</th><th>Category</th><th>Status</th><th>Stock</th><th>Value (cost)</th><th>Sold</th><th>Last sale</th></tr></thead>
<tbody>${rows}</tbody>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
